import { Patology, PatologyQuery } from '../models/Patology'
import { Container, IInit } from '../../../common/container/Container'
import { ContentContainerConfig } from '../container'
import { Observable, of } from 'rxjs'
import { prepareURL } from '../../../common/api/http-helpers'
import { catchError, map } from 'rxjs/operators'
import { Query } from '../../../common/api/Query'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { toModel, PatologyDTO } from '../models/PatologyDTO'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { STATUS_SERVICE_KEY } from '../../../container/app'

export interface IpatologyApi extends IInit {
  getByID(id: string | null): Observable<Patology | undefined>

  getFilteredList(q: Query<PatologyQuery>): Observable<ItemList<Patology>>

  add(e: PatologyDTO): Observable<Patology | undefined>

  update(e: PatologyDTO): Observable<Patology | undefined>

  delete(id: string): Observable<boolean>
}

export class PatologyApi implements IpatologyApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as ContentContainerConfig).moduleFullUrl + '/patology'
  }

  getByID(id: string | null): Observable<Patology | undefined> {
    return this._httpClient
      .get<Patology>({ url: this._url + '/' + id })
      .pipe(
        map<PatologyDTO, Patology>((d) => toModel(d)),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  add(e: PatologyDTO): Observable<Patology | undefined> {
    return this._httpClient
      .post<Patology>({ url: this._url, body: e })
      .pipe(
        map<PatologyDTO, Patology>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getFilteredList(q: Query<PatologyQuery>): Observable<ItemList<Patology>> {
    return this._httpClient
      .get<ItemList<Patology>>({ url: prepareURL(this._url, q) })
      .pipe(
        map<ItemList<PatologyDTO>, ItemList<Patology>>((dto) => {
          const itemList = emptyList<Patology>()
          itemList.items = dto.items.map((d) => toModel(d))
          itemList.count = dto.count
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<Patology>())
        })
      )
  }

  update(e: PatologyDTO): Observable<Patology | undefined> {
    return this._httpClient
      .put<Patology>({ url: this._url, body: e })
      .pipe(
        map<PatologyDTO, Patology>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }
}
